import React, { useEffect, useState } from 'react';
import {
  DetailedPlayer,
  DbQuestionData,
  ReportInaccuracy,
  VanquishedDemon,
} from '../../types';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Typography,
  Link,
  Box,
  styled,
  tableCellClasses,
  useTheme,
  Tooltip,
  Switch,
  FormControl,
  FormControlLabel,
  FormGroup,
  Button,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// import GaugeChart from 'react-gauge-chart';
import { ScorecardLegend } from '../../../../dialog/ScorecardLegend';
import { ScorecardTableRow } from './ScorecardTableRow';
import { fetchPost } from '../../../../api/common';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import ShareIcon from '@mui/icons-material/Share';
import { ScoreChart } from '../../../../dialog/ScoreChart';
import LinkIcon from '@mui/icons-material/Link';
import { BlinkingChatButton } from '../../game/score-screen/BlinkingChatButton';
import ListIcon from '@mui/icons-material/List';
import { GiRank3 } from 'react-icons/gi';
import { track } from '../../../../api/track';
import {
  CATEGORY_SETS_LABEL,
  MODE_DISPLAY_NAME,
  PRO_CASUAL_DISPLAY,
} from '../../../../common/constants';
import { ImageDisplay } from '../../../../dialog/ImageDisplay';
import { LastSurvivalQuestionStatsDialog } from '../../../../dialog/LastSurvivalQuestionStatsDialog';
import { fetchGet } from '@par/shared/api/common';
import { QuestionStatsLean } from '@par/shared/types/question-stats';
import { StarterKitConfig } from '../../../starter-kits/types';
import { Mode } from '@par/shared/common/types';
import { Team } from '@par/shared/types/game';

export const PlayerTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.root}`]: {
    width: '100px',
    height: '50px',
    borderLeft: '1px solid #333333',
    borderRight: '1px solid #333333',
  },
}));

export const formattedScore = (score: number) =>
  score > 0 ? `+${score}` : score;

type PropTypes = {
  averageDifficulty?: number;
  bannedCategories?: string[];
  categorySelections?: string[];
  categorySet?: string;
  centerSnackbar?: boolean;
  demons?: boolean;
  demonsVanquished?: VanquishedDemon[];
  detailedPlayers?: Record<string, DetailedPlayer>;
  enableReportInaccuracy?: boolean;
  gameType?: 'casual' | 'pro';
  handicap?: number;
  handleMmrResultsClick?: () => void;
  handleOpenChatClick?: () => void;
  handleReportInaccuracyRowClick?: (
    row: DbQuestionData,
    questionIndex: number
  ) => void;
  isDaily?: boolean;
  isGhostChallenge?: boolean;
  isMatchMaking?: boolean;
  showDislike?: boolean;
  showRatings?: boolean;
  forPublic?: boolean;
  gameId?: string;
  hideTitle?: boolean;
  hideUsernameLink?: boolean;
  league?: boolean;
  mode?: Mode;
  myTeam?: Team;
  otherTeam?: Team;
  profileId?: string;
  questionSet: DbQuestionData[];
  ranked?: boolean;
  reportedInaccuracies?: ReportInaccuracy[];
  seededBy?: string;
  selectedPlayer?: DetailedPlayer;
  showChat?: boolean;
  showMmrResults?: boolean;
  starterKit?: StarterKitConfig;
  survivalMode?: 'endurance' | 'challenge' | 'demon';
  tournament?: number;
  unreadChat?: boolean;
  variant?: 'normal' | 'survival' | 'starter-kit' | 'turbo';
};

export const ScorecardMulti = ({
  averageDifficulty,
  bannedCategories,
  categorySelections,
  categorySet,
  centerSnackbar,
  demons,
  demonsVanquished,
  detailedPlayers,
  enableReportInaccuracy,
  forPublic,
  gameId,
  gameType,
  handicap,
  handleMmrResultsClick,
  handleOpenChatClick,
  handleReportInaccuracyRowClick,
  isDaily,
  isGhostChallenge,
  isMatchMaking,
  showDislike,
  showRatings,
  hideTitle,
  hideUsernameLink,
  league,
  mode,
  myTeam,
  otherTeam,
  profileId,
  questionSet,
  ranked,
  reportedInaccuracies,
  seededBy,
  selectedPlayer,
  showChat,
  showMmrResults,
  starterKit,
  survivalMode,
  tournament,
  unreadChat,
  variant,
}: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [playerSelf, setPlayerSelf] = useState<DetailedPlayer>();
  const [playerThem, setPlayerThem] = useState<DetailedPlayer>();
  const [showAllScores, setShowAllScores] = useState(false);
  const [runningTotals, setRunningTotals] = useState(false);
  const [openLegend, setOpenLegend] = useState(false);
  const [questionDisliked, setQuestionDisliked] = useState<string[]>([]); // string of questionIds
  const [questionLiked, setQuestionLiked] = useState<string[]>([]); // string of questionIds
  const [openScoreChart, setOpenScoreChart] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [revealAnswers, setRevealAnswers] = useState(new Array(10).fill(false));
  const [openQuestionImage, setOpenQuestionImage] = useState(false);
  const [questionImgUrl, setQuestionImgUrl] = useState<string>();
  const [openLastQuestionStats, setOpenLastQuestionStats] = useState(false);
  const [lastQuestionStats, setLastQuestionStats] = useState<any>();
  const [questionStats, setQuestionStats] = useState<QuestionStatsLean[]>([]);
  const [loadingQuestionStats, setLoadingQuestionStats] = useState(true);
  const [displayQuestionStatSeen, setDisplayQuestionStatSeen] = useState(false);
  const [customQuestionComments, setCustomQuestionComments] = useState<
    { questionId: string; authorComment: string; referenceLink: string }[]
  >([]);

  useEffect(() => {
    fetchQuestionStats();
    fetchCustomQuestionComments();
  }, []);

  useEffect(() => {
    if (!detailedPlayers) return;
    if (mode === 'match') {
      setPlayerThem(Object.values(detailedPlayers)[1]);
    }
    if (mode !== 'sit-n-go') {
      setPlayerSelf(Object.values(detailedPlayers)[0]);
    }
  }, [detailedPlayers]);

  const fetchQuestionStats = async () => {
    const res = await fetchGet(`/question-stats/game/${gameId}/${profileId}`);
    setLoadingQuestionStats(false);
    if (res.message) return;
    setQuestionStats(res);
    const dislikedAQuestionInSet = res.filter((qs) =>
      qs?.dislikedBy?.includes(profileId)
    );
    if (dislikedAQuestionInSet.length) {
      setQuestionDisliked((prev) => prev.concat(res.questionId));
    }
  };

  const fetchCustomQuestionComments = async () => {
    const res = await fetchGet(`/custom-questions/links/${gameId}`);
    if (res && res.length) {
      setCustomQuestionComments(res);
    }
  };

  const handleUsernameClick = async (e: any, profileId?: string) => {
    if (!profileId) return;
    e.stopPropagation();
    navigate(`/profile/overview/${profileId}`);
  };

  const calcDifficultyGauge = (averageDifficulty: number) =>
    Math.round(33.33 * averageDifficulty - 50) / 100;

  const handleDislikeQuestion = async (questionId: string) => {
    const res = await fetchPost('/stats/dislike/', {
      questionId: questionId,
      profileId: profileId,
    });
    setQuestionDisliked((prev) => prev.concat(questionId));
  };

  const handleLikeQuestion = async (questionId: string) => {
    const res = await fetchPost('/stats/like/', {
      questionId: questionId,
      profileId: profileId,
    });
    setQuestionLiked((prev) => prev.concat(questionId));
  };

  const handleShareClick = async () => {
    navigator.clipboard.writeText(`https://app.par.live/game/view/${gameId}`);
    setOpenToast(true);
    await track(profileId || null, 'scorecard.share.button');
  };

  const tableCellsTotals = (
    <>
      {enableReportInaccuracy ? <TableCell width={'2%'}></TableCell> : null}
      <TableCell
        colSpan={
          showDislike || showRatings ? 7 : !forPublic && !starterKit ? 6 : 5
        }
        sx={{
          textAlign: 'right',
          fontWeight: 600,
        }}
      >
        <Typography variant="h6">Totals:</Typography>
      </TableCell>
      {mode !== 'teams' && playerSelf ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {formattedScore(playerSelf.parScore)}
          {/* {handicap && seededBy !== playerSelf.profileId
            ? ` (${handicap > 0 ? '+' : ''}${handicap})`
            : ''} */}
        </PlayerTableCell>
      ) : null}
      {mode !== 'teams' && playerThem ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {playerThem.parScore !== undefined
            ? formattedScore(playerThem.parScore)
            : 'N/A'}
          {/* {handicap && seededBy !== playerThem.profileId
            ? ` (${handicap > 0 ? '+' : ''}${handicap})`
            : ''} */}
        </PlayerTableCell>
      ) : null}
      {mode === 'teams' && myTeam ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {formattedScore(myTeam.parScore)}
        </PlayerTableCell>
      ) : null}
      {mode === 'teams' && otherTeam ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {formattedScore(otherTeam.parScore)}
        </PlayerTableCell>
      ) : null}
      {mode === 'sit-n-go' && selectedPlayer ? (
        <PlayerTableCell
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 20,
          }}
        >
          {selectedPlayer.parScore !== undefined
            ? formattedScore(selectedPlayer.parScore)
            : 'N/A'}
        </PlayerTableCell>
      ) : null}
    </>
  );
  const handleOpenScoreChartClick = () => setOpenScoreChart(true);

  const toastMessage = (
    <Stack direction="row" spacing={1}>
      <LinkIcon />
      <Typography fontWeight={600}>Link copied to clipboard</Typography>
    </Stack>
  );

  const shareTooltip = (
    <Stack spacing={1}>
      <Typography
        variant="body1"
        sx={{
          color:
            theme.palette.mode === 'light'
              ? theme.palette.secondary.main
              : theme.palette.text.primary,
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 1px ${theme.palette.secondary.main}, 10px 0px 20px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Won on a great answer?
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color:
            theme.palette.mode === 'light'
              ? theme.palette.secondary.main
              : theme.palette.text.primary,
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 1px ${theme.palette.secondary.main}, 10px 0px 20px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Beat a TV celebrity?
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color:
            theme.palette.mode === 'light'
              ? theme.palette.secondary.main
              : theme.palette.text.primary,
          textShadow:
            theme.palette.mode === 'dark'
              ? `0 0 1px ${theme.palette.secondary.main}, 10px 0px 20px ${theme.palette.secondary.main}, 0px 0px 16px ${theme.palette.secondary.main}`
              : 'none',
        }}
      >
        Share with friends!
      </Typography>
    </Stack>
  );

  const handleRevealAnswerClick = (index: number) => {
    const updated = Object.assign([], revealAnswers, { [index]: true });
    setRevealAnswers(updated);
  };

  const handleImageClick = (imgUrl: string) => {
    setQuestionImgUrl(imgUrl);
    setOpenQuestionImage(true);
  };

  const handleLastQuestionClick = async (questionId: string) => {
    const res = await fetchGet(`/stats/question/${questionId}`);
    setLastQuestionStats(res);
    setOpenLastQuestionStats(true);
  };

  const generateGameModeLabel = () => {
    let label = '';
    if (tournament) {
      return 'Tournament';
    }
    if (demons) {
      return 'Face Your Demons';
    }
    if (starterKit) {
      return starterKit.starterKitName;
    }
    if (mode !== 'solo' && ranked) {
      label = label + 'Ranked - ';
    }
    if (mode !== 'solo' && !ranked) {
      label = label + 'Friendly - ';
    }
    label = label + MODE_DISPLAY_NAME[mode] + ' - ';
    if (variant !== 'survival') {
      label = label + PRO_CASUAL_DISPLAY[gameType] + ' - ';
    }
    if (variant !== 'survival') {
      if (categorySet === 'none') {
        if (categorySelections?.length === 1) {
          label = label + categorySelections.join(' ');
        } else {
          label = label + 'Mix';
        }
      } else {
        label = label + (CATEGORY_SETS_LABEL[categorySet] || '');
      }
    }
    if (variant === 'survival') {
      if (survivalMode === 'challenge') {
        label = label + 'Survival - Challenge Tower';
      }
      if (survivalMode === 'endurance') {
        label = label + 'Survival - Endurance';
      }
    }
    return label;
  };

  const handleMouseOverBarChart = (mouseover: boolean) => {
    setDisplayQuestionStatSeen(mouseover);
  };

  const getTeamMemberUsernames = (team: Team) => {
    const username = Object.values(detailedPlayers)
      .filter((p) => team.members.includes(p.profileId))
      .map((p) => p.username);
    return (
      <Stack>
        {username.map((name) => (
          <Typography variant="subtitle2">{name}</Typography>
        ))}
      </Stack>
    );
  };

  return (
    <>
      {lastQuestionStats ? (
        <LastSurvivalQuestionStatsDialog
          close={() => setOpenLastQuestionStats(false)}
          open={openLastQuestionStats}
          questionStats={lastQuestionStats}
        />
      ) : null}
      {questionImgUrl ? (
        <ImageDisplay
          closeImageDisplay={() => setOpenQuestionImage(false)}
          imgSource={questionImgUrl}
          openImageDisplay={openQuestionImage}
        />
      ) : null}
      <ScoreChart
        closeScoreChart={() => setOpenScoreChart(false)}
        openScoreChart={openScoreChart}
        mode={mode}
        myTeam={myTeam}
        otherTeam={otherTeam}
        players={detailedPlayers}
        questionSet={questionSet}
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: centerSnackbar ? 'center' : 'left',
        }}
        open={openToast}
        onClose={() => setOpenToast(false)}
        autoHideDuration={4000}
      >
        <SnackbarContent
          style={{
            backgroundColor: theme.palette.tertiary.light,
          }}
          message={toastMessage}
        />
      </Snackbar>
      <ScorecardLegend open={openLegend} close={() => setOpenLegend(false)} />
      <Stack width="100%">
        <Stack direction={'row'}>
          <>
            <Box width={'25%'}></Box>
            <Stack spacing={1} sx={{ width: '50%' }}>
              {!hideTitle ? (
                <Typography
                  variant={'h3'}
                  textAlign="center"
                  sx={{ letterSpacing: 10 }}
                >
                  SCORECARD
                </Typography>
              ) : null}
              <Typography
                variant="caption"
                textAlign="center"
                sx={{ color: theme.palette.tertiary.main }}
              >
                {generateGameModeLabel()}
              </Typography>
            </Stack>
            <Box
              width={'25%'}
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'right',
              }}
            >
              {!isDaily ? (
                <Tooltip title={shareTooltip} placement="left">
                  <Button
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={handleShareClick}
                    size="large"
                    color="primary"
                    sx={{ position: 'absolute', bottom: 0 }}
                  >
                    Share
                  </Button>
                </Tooltip>
              ) : null}
            </Box>
          </>
        </Stack>
        <Stack
          direction={'row'}
          spacing={5}
          sx={{
            display: 'flex',
            justifyContent: 'right',
            marginTop: '20px',
            paddingY: '5px',
            paddingX: '10px',
            border: '1px solid',
            borderRadius: '5px',
            borderColor: theme.palette.secondary.dark,
          }}
        >
          <Stack direction={'row'} spacing={2} width={'60%'}>
            <Button
              variant="text"
              onClick={handleOpenScoreChartClick}
              startIcon={<SsidChartIcon />}
            >
              Game Flow
            </Button>
            <Button
              variant="text"
              onClick={() => setOpenLegend(true)}
              startIcon={<ListIcon />}
            >
              Legend
            </Button>
            {/* {showChat && !isMatchMaking && !isGhostChallenge ? (
              <BlinkingChatButton
                handleOpenChatClick={handleOpenChatClick}
                unreadChat={unreadChat}
              ></BlinkingChatButton>
            ) : null} */}
            {ranked &&
            Object.keys(detailedPlayers).length >= 2 &&
            showMmrResults ? (
              <Button
                onClick={handleMmrResultsClick}
                sx={{
                  color: theme.palette.ranked.main,
                  '&:hover': {
                    backgroundColor: 'rgb(57 139 233 / 12%)',
                    borderColor: theme.palette.ranked.main,
                  },
                }}
                startIcon={<GiRank3 />}
              >
                MMR
              </Button>
            ) : null}
            {averageDifficulty ? (
              <Tooltip
                title={`Question set difficulty is ${Math.round(
                  calcDifficultyGauge(averageDifficulty) * 100
                )}%`}
                placement="top"
              >
                <Box sx={{ width: '120px' }}>
                  {/* <GaugeChart
                    nrOfLevels={10}
                    needleColor={theme.palette.info.main}
                    percent={calcDifficultyGauge(averageDifficulty)}
                    fontSize="16px"
                    style={{ width: '120px' }}
                  /> */}
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
          <Box width={'40%'} sx={{ display: 'flex', justifyContent: 'right' }}>
            <FormControl component="fieldset">
              <FormGroup aria-label="position" row>
                {mode === 'sit-n-go' ? (
                  <FormControlLabel
                    value="showAllScores"
                    control={
                      <Tooltip
                        title="Click a single row to show scores for only that row"
                        placement="top"
                      >
                        <Switch
                          checked={showAllScores}
                          onChange={() => setShowAllScores((prev) => !prev)}
                        />
                      </Tooltip>
                    }
                    label={
                      <Typography variant="subtitle2">
                        Show all scores
                      </Typography>
                    }
                    labelPlacement="start"
                  />
                ) : null}
                <FormControlLabel
                  value="runningTotals"
                  control={
                    <Switch
                      checked={runningTotals}
                      onChange={() => setRunningTotals((prev) => !prev)}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">Running totals</Typography>
                  }
                  labelPlacement="start"
                />
              </FormGroup>
            </FormControl>
          </Box>
        </Stack>
        {bannedCategories && bannedCategories.length > 0 ? (
          <Stack
            direction={'row'}
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'left',
              marginTop: 1,
              marginBottom: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Bans:
            </Typography>
            {bannedCategories.map((c) => (
              <Typography
                variant="caption"
                className="angled-strikethrough straight"
                sx={{
                  color: '#b0917f',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {c}
              </Typography>
            ))}
          </Stack>
        ) : null}
        <Box sx={{ padding: 1 }}>
          <Paper elevation={3}>
            <TableContainer component={Paper}>
              <Table
                size="small"
                aria-label="simple table"
                sx={{ minWidth: 1000 }}
              >
                <TableHead>
                  <TableRow>
                    {enableReportInaccuracy ? (
                      <TableCell width={'2%'} style={{ top: 50 }}></TableCell>
                    ) : null}
                    {showDislike || showRatings ? (
                      <TableCell width="2%"></TableCell>
                    ) : null}
                    {!starterKit ? <TableCell width="2%"></TableCell> : null}
                    {!forPublic ? <TableCell width="2%"></TableCell> : null}
                    <TableCell>
                      <Typography variant={'body1'} fontWeight={600}>
                        Question
                      </Typography>
                    </TableCell>
                    <TableCell width="20%">
                      <Typography
                        variant={'body1'}
                        fontWeight={600}
                        sx={{ color: theme.palette.dailies.main }}
                      >
                        Answer
                      </Typography>
                    </TableCell>
                    <TableCell width="10%">
                      <Typography variant={'body1'} fontWeight={600}>
                        Category
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant={'body1'}
                        textAlign={'left'}
                        fontWeight={600}
                      >
                        PAR
                      </Typography>
                    </TableCell>
                    {mode !== 'teams' && playerSelf ? (
                      <PlayerTableCell>
                        <Tooltip title={playerSelf.username} placement="top">
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            textAlign={'center'}
                            width={'60px'}
                            sx={{
                              color: theme.palette.primary.main,
                              wordBreak: 'break-all',
                            }}
                          >
                            {hideUsernameLink ? (
                              playerSelf.username.length > 13 ? (
                                playerSelf.username.slice(0, 13) + '.'
                              ) : (
                                playerSelf.username
                              )
                            ) : (
                              <Link
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) =>
                                  handleUsernameClick(e, playerSelf?.profileId)
                                }
                              >
                                {playerSelf.username.length > 13
                                  ? playerSelf.username.slice(0, 13) + '.'
                                  : playerSelf.username}
                              </Link>
                            )}
                          </Typography>
                        </Tooltip>
                      </PlayerTableCell>
                    ) : null}
                    {mode !== 'teams' && playerThem ? (
                      <PlayerTableCell>
                        <Tooltip title={playerThem.username} placement="top">
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            textAlign={'center'}
                            width={'60px'}
                            sx={{
                              color: theme.palette.primary.main,
                              wordBreak: 'break-all',
                            }}
                          >
                            {hideUsernameLink ? (
                              playerThem.username.length > 13 ? (
                                playerThem.username.slice(0, 13) + '.'
                              ) : (
                                playerThem.username
                              )
                            ) : (
                              <Link
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) =>
                                  handleUsernameClick(e, playerThem?.profileId)
                                }
                              >
                                {playerThem.username.length > 13
                                  ? playerThem.username.slice(0, 13) + '.'
                                  : playerThem.username}
                              </Link>
                            )}
                          </Typography>
                        </Tooltip>
                      </PlayerTableCell>
                    ) : null}
                    {mode === 'teams' && myTeam ? (
                      <PlayerTableCell>
                        <Tooltip
                          title={getTeamMemberUsernames(myTeam)}
                          placement="top"
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            textAlign={'center'}
                            width={'60px'}
                            sx={{
                              color: theme.palette.primary.main,
                              wordBreak: 'break-all',
                            }}
                          >
                            {myTeam.username}
                          </Typography>
                        </Tooltip>
                      </PlayerTableCell>
                    ) : null}
                    {mode === 'teams' && otherTeam ? (
                      <PlayerTableCell>
                        <Tooltip
                          title={getTeamMemberUsernames(otherTeam)}
                          placement="top"
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            textAlign={'center'}
                            width={'60px'}
                            sx={{
                              color: theme.palette.primary.main,
                              wordBreak: 'break-all',
                            }}
                          >
                            {otherTeam.username}
                          </Typography>
                        </Tooltip>
                      </PlayerTableCell>
                    ) : null}
                    {mode === 'sit-n-go' && selectedPlayer ? (
                      <PlayerTableCell>
                        <Tooltip
                          title={selectedPlayer.username}
                          placement="top"
                        >
                          <Typography
                            variant={'subtitle2'}
                            fontWeight={600}
                            textAlign={'center'}
                            width={'60px'}
                            sx={{
                              color: theme.palette.primary.main,
                              wordBreak: 'break-all',
                            }}
                          >
                            {hideUsernameLink ? (
                              selectedPlayer.username.length > 13 ? (
                                selectedPlayer.username.slice(0, 13) + '.'
                              ) : (
                                selectedPlayer.username
                              )
                            ) : (
                              <Link
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) =>
                                  handleUsernameClick(
                                    e,
                                    selectedPlayer?.profileId
                                  )
                                }
                              >
                                {selectedPlayer.username.length > 13
                                  ? selectedPlayer.username.slice(0, 13) + '.'
                                  : selectedPlayer.username}
                              </Link>
                            )}
                          </Typography>
                        </Tooltip>
                      </PlayerTableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questionSet.map((q, index) => (
                    <ScorecardTableRow
                      customQuestionComment={customQuestionComments?.find(
                        (cq) => cq.questionId === q._id
                      )}
                      detailedPlayers={detailedPlayers}
                      demons={demons}
                      demonsVanquished={demonsVanquished}
                      displayQuestionStatSeen={displayQuestionStatSeen}
                      enableReportInaccuracy={enableReportInaccuracy}
                      handicap={handicap}
                      handleDislikeQuestion={handleDislikeQuestion}
                      handleLikeQuestion={handleLikeQuestion}
                      handleImageClick={handleImageClick}
                      handleLastQuestionClick={handleLastQuestionClick}
                      handleMouseOverBarChart={handleMouseOverBarChart}
                      handleReportInaccuracyRowClick={
                        handleReportInaccuracyRowClick
                      }
                      handleRevealAnswerClick={handleRevealAnswerClick}
                      hideAnswers={forPublic}
                      hideStats={forPublic || !!starterKit}
                      index={index}
                      league={league}
                      loadingQuestionStats={loadingQuestionStats}
                      mode={mode}
                      myTeam={myTeam}
                      otherTeam={otherTeam}
                      playerSelf={playerSelf}
                      playerThem={playerThem}
                      profileId={profileId}
                      question={q}
                      questionStats={questionStats?.find(
                        (qs) => qs.questionId === q._id
                      )}
                      questionDisliked={questionDisliked}
                      questionLiked={questionLiked}
                      reportedInaccuracies={reportedInaccuracies}
                      revealAnswer={revealAnswers[index]}
                      runningTotals={runningTotals}
                      seededBy={seededBy}
                      selectedPlayer={selectedPlayer}
                      showAllScores={showAllScores}
                      showDislike={showDislike}
                      showRatings={showRatings}
                      totalQuestions={questionSet.length}
                      variant={variant}
                    />
                  ))}
                  {mode === 'solo' ||
                  mode === 'match' ||
                  mode === 'teams' ||
                  (mode === 'sit-n-go' && selectedPlayer) ? (
                    <TableRow>{tableCellsTotals}</TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            {handicap && playerSelf && playerThem ? (
              <Typography textAlign={'right'}>
                {seededBy === playerSelf.profileId
                  ? playerThem.username
                  : playerSelf.username}{' '}
                started with a {handicap > 0 ? '+' : ''}
                {handicap} {handicap < 0 ? 'advantage' : 'disadvantage'}
              </Typography>
            ) : null}
          </Paper>
        </Box>
      </Stack>
    </>
  );
};
